import {
  OPS_DOCUMENT_MAX_BYTES,
  OPS_INSTALMENTS_MAX,
  OPS_ISSUE_MAX_DOCUMENTS,
  OPS_QC_CURRENCIES,
  OPS_PRICE_INCREASE_LIMIT_INR,
} from '@utazzo/shared';
import {
  cn,
  UzBadge,
  UzButton,
  UzConfirmDialog,
  UzDatePicker,
  UzDialog,
  UzDrawer,
  UzInput,
  UzSelect,
  UzSpinner,
  UzTabs,
  UzTextarea,
} from '@utazzo/ui';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileImage,
  FolderOpen,
  HandCoins,
  Lock,
  Paperclip,
  Send,
  PauseCircle,
  RotateCcw,
  Undo2,
  Upload,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';

import { fileToBase64 } from '../file-base64';
import { itemKindIcon, itemKindLabel } from '../item-kind';
import { payByTone } from '../pay-by';
import { transferRouteLabel } from '../transfer-route';
import {
  useDeleteOpsCaseDocument,
  useHoldOpsCaseItem,
  useIssueOpsCaseItem,
  useOpenOpsDocument,
  useOpenOpsProof,
  useOpenOpsSellerScreenshot,
  useOpsCaseDocuments,
  useOpsSellerScreenshots,
  useOpsCaseItems,
  useUploadOpsCaseDocument,
  useRequestOpsAmendment,
  useRequestOpsFund,
  useResetOpsCaseItem,
} from '../use-ops-cases';

import {
  AUDIENCE_OPTIONS,
  DOC_ACCEPT,
  DocumentsLoading,
  KIND_OPTIONS,
  OpsDocumentUploadForm,
  OpsDocumentsGroup,
} from './OpsDocumentsPanel';

import type {
  OpsCaseItem,
  OpsCaseRow,
  OpsDocumentAudience,
  OpsDocumentFileInput,
  OpsDocumentKind,
} from '@utazzo/shared';

import { SupplierPicker } from '@/components/pickers';
import { useCurrentUser } from '@/features/auth/use-auth';
import * as suppliersApi from '@/features/suppliers/suppliers.api';

/**
 * Bookings for one converted lead: every flight, hotel, activity, transfer
 * (and visa / cruise) on the sold itinerary. Per item the ops executive can:
 *
 *   Hold         - QC price, holding number, last date of payment, and how
 *                  the supplier is paid: in full or in instalments.
 *   Ask for fund - Accounts pays the supplier (every instalment goes at once,
 *                  each with its own pay-by). If the lead's total price
 *                  increase goes over INR 500 it first needs Admin / TL.
 *   Issue        - after Accounts paid: the supplier's confirmation number.
 *   Not available- back to the seller for an amendment with the customer.
 */

type Currency = (typeof OPS_QC_CURRENCIES)[number];
type Mode = 'none' | 'hold' | 'fund' | 'issue' | 'amend';
/** One instalment as typed on the hold form. */
interface PlanRow {
  amount: string;
  dueDate: string;
  label: string;
}

/** A file picked in the Issue form, before it is read and sent. */
interface AttachmentDraft {
  file: File;
  kind: Exclude<OpsDocumentKind, 'PAYMENT_PROOF'>;
  audience: OpsDocumentAudience;
}

export const STATUS_META: Record<
  OpsCaseItem['status'],
  {
    label: string;
    tone: 'default' | 'secondary' | 'success' | 'warning' | 'destructive';
    border: string;
    icon: string;
  }
> = {
  PENDING: {
    label: 'To book',
    tone: 'secondary',
    border: 'border-zinc-200',
    icon: 'bg-zinc-100 text-zinc-600',
  },
  HOLD: {
    label: 'On hold',
    tone: 'warning',
    border: 'border-amber-200',
    icon: 'bg-amber-100 text-amber-700',
  },
  APPROVAL_PENDING: {
    label: 'Waiting for approval',
    tone: 'warning',
    border: 'border-orange-200',
    icon: 'bg-orange-100 text-orange-700',
  },
  APPROVAL_REJECTED: {
    label: 'Price rejected',
    tone: 'destructive',
    border: 'border-red-200',
    icon: 'bg-red-100 text-red-700',
  },
  FUND_REQUESTED: {
    label: 'With Accounts',
    tone: 'default',
    border: 'border-sky-200',
    icon: 'bg-sky-100 text-sky-700',
  },
  FUND_PAID: {
    label: 'Paid - confirm & issue',
    tone: 'default',
    border: 'border-indigo-200',
    icon: 'bg-indigo-100 text-indigo-700',
  },
  ISSUED: {
    label: 'Issued',
    tone: 'success',
    border: 'border-emerald-200',
    icon: 'bg-emerald-100 text-emerald-700',
  },
  AMENDMENT_APPROVAL: {
    label: 'Not available - admin approval',
    tone: 'warning',
    border: 'border-rose-200',
    icon: 'bg-rose-100 text-rose-700',
  },
  AMENDMENT: {
    label: 'With seller (amendment)',
    tone: 'destructive',
    border: 'border-rose-200',
    icon: 'bg-rose-100 text-rose-700',
  },
};

const CURRENCY_OPTIONS = OPS_QC_CURRENCIES.map((c) => ({ value: c, label: c }));

const fmtDate = (d: Date | null): string =>
  d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const money = (n: number | null, currency: string): string =>
  n === null
    ? '-'
    : `${currency} ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n)}`;

const inr = (n: number): string =>
  `INR ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(n))}`;

/** Local calendar day as YYYY-MM-DD (not UTC, so an IST evening stays on its date). */
const isoDay = (d: Date | null): string =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';
const fromIsoDay = (s: string): Date | null =>
  /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : null;

/**
 * Body of one action form. The forms open in a dialog (title shown there), so
 * this only keeps the tinted frame that tells hold / fund / issue / amend apart.
 */
const FormShell = ({
  title,
  tone,
  children,
}: {
  title: string;
  tone: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div className={cn('rounded-lg border p-3', tone)} aria-label={title}>
    {children}
  </div>
);

const MODE_TITLE: Record<Exclude<Mode, 'none'>, { title: string; description: string }> = {
  hold: {
    title: 'Put on hold',
    description:
      'QC price, the holding number from the supplier, the last date to pay - and the payment plan if the supplier takes instalments.',
  },
  fund: {
    title: 'Issue - ask Accounts for fund',
    description:
      'Accounts pays the supplier and uploads the screenshot; you then confirm and issue.',
  },
  issue: {
    title: 'Issue - supplier confirmed',
    description: 'Enter the confirmation number and attach the ticket / voucher for the customer.',
  },
  amend: {
    title: 'Not available - send for amendment',
    description:
      'An admin approves the itinerary change, then the seller changes it with the customer.',
  },
};

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

const HoldForm = ({
  item,
  caseId,
  onDone,
}: {
  item: OpsCaseItem;
  caseId: string;
  onDone: () => void;
}): React.JSX.Element => {
  const hold = useHoldOpsCaseItem(caseId);
  const [qcPrice, setQcPrice] = useState(item.qcPrice !== null ? String(item.qcPrice) : '');
  const [currency, setCurrency] = useState<Currency>((item.currency as Currency) ?? 'INR');
  const [holdReference, setHoldReference] = useState(item.holdReference ?? '');
  const [paymentDueDate, setPaymentDueDate] = useState(isoDay(item.paymentDueDate));
  const [note, setNote] = useState(item.note ?? '');
  // Supplier from the master; the name rides on the item and prefills the fund request.
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState(item.supplierName ?? '');
  const pickSupplier = (id: string | null): void => {
    setSupplierId(id);
    if (!id) {
      setSupplierName('');
      return;
    }
    void suppliersApi
      .getSupplier(id)
      .then((sup) => setSupplierName(sup.name))
      .catch(() => setSupplierName(''));
  };
  // Payment plan: off = one full payment by the last date; on = instalments
  // adding up to the QC price, each with the supplier's deadline for it. A
  // saved plan comes back with its pay-by (one day before the deadline), so
  // it is shown a day later - the deadline the operator typed.
  const hadPlan = item.instalments.length > 1;
  const [split, setSplit] = useState(hadPlan);
  const [plan, setPlan] = useState<PlanRow[]>(
    hadPlan
      ? item.instalments.map((i) => ({
          amount: String(i.amount),
          dueDate: isoDay(new Date(i.dueDate.getTime() + 24 * 60 * 60 * 1000)),
          label: i.label ?? '',
        }))
      : [
          { amount: '', dueDate: '', label: 'Deposit' },
          { amount: '', dueDate: '', label: 'Balance' },
        ],
  );
  const setRow = (idx: number, patch: Partial<PlanRow>): void =>
    setPlan((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const [tried, setTried] = useState(false);

  const priceOk = Number(qcPrice) > 0;
  const refOk = holdReference.trim().length > 0;
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(paymentDueDate);
  const planSum = plan.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const planRowsOk = plan.every(
    (r) => Number(r.amount) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(r.dueDate),
  );
  const planSumOk = Math.abs(planSum - Number(qcPrice)) < 0.01;
  const planOk = !split || (planRowsOk && planSumOk);

  const submit = (): void => {
    setTried(true);
    if (!priceOk || !refOk || !dateOk || !planOk) return;
    hold.mutate(
      {
        componentId: item.componentId,
        input: {
          qcPrice: Number(qcPrice),
          currency,
          holdReference: holdReference.trim(),
          paymentDueDate,
          ...(supplierName.trim() ? { supplierName: supplierName.trim() } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
          // Off = no rows: the server drops any earlier plan (one full payment).
          ...(split
            ? {
                instalments: plan.map((r) => ({
                  amount: Number(r.amount),
                  dueDate: r.dueDate,
                  ...(r.label.trim() ? { label: r.label.trim() } : {}),
                })),
              }
            : {}),
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <FormShell title="Put on hold" tone="border-amber-200 bg-amber-50/50 text-amber-900">
      <div className="grid gap-2 sm:grid-cols-[1fr_96px]">
        <UzInput
          label="QC price"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          required
          value={qcPrice}
          onChange={(e) => setQcPrice(e.target.value)}
          error={tried && !priceOk ? 'Enter the price confirmed with the supplier' : undefined}
        />
        <UzSelect<Currency>
          label="Currency"
          value={currency}
          onValueChange={setCurrency}
          options={CURRENCY_OPTIONS}
        />
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <UzInput
          label="Holding number"
          required
          placeholder="PNR / option / hold ref"
          value={holdReference}
          onChange={(e) => setHoldReference(e.target.value)}
          error={tried && !refOk ? 'Holding number is required' : undefined}
        />
        <SupplierPicker
          label="Supplier"
          value={supplierId}
          onValueChange={pickSupplier}
          currentName={supplierName}
          hint={
            !supplierId && item.supplierName
              ? 'Pick another supplier to change.'
              : 'Who the hold is with - from the supplier master.'
          }
        />
        <UzDatePicker
          label="Supplier's last date of payment"
          required
          value={fromIsoDay(paymentDueDate)}
          onValueChange={(d) => setPaymentDueDate(isoDay(d))}
          minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
          hint={
            dateOk
              ? `Saved as pay-by ${fmtDate(new Date(new Date(`${paymentDueDate}T00:00:00.000Z`).getTime() - 24 * 60 * 60 * 1000))} (one day earlier, so we pay before the hold lapses). Reminders start 3 days before.`
              : 'Stored one day earlier as the pay-by date; reminders start 3 days before it.'
          }
          error={tried && !dateOk ? 'Pick the last payment date' : undefined}
        />
      </div>
      {/* How the supplier is paid: in full by the date above, or in instalments. */}
      <div className="mt-3 rounded-lg border border-amber-200/70 bg-white/60 p-2.5">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-800">
          <input
            type="checkbox"
            className="h-4 w-4 accent-amber-600"
            checked={split}
            onChange={(e) => setSplit(e.target.checked)}
          />
          Pay the supplier in instalments
          <span className="text-xs text-zinc-500">(off = the full QC price by the date above)</span>
        </label>
        {split ? (
          <div className="mt-2 space-y-2">
            {plan.map((row, idx) => (
              <div key={idx} className="grid items-end gap-2 sm:grid-cols-[28px_1fr_1fr_1fr_32px]">
                <span className="pb-2.5 text-xs text-zinc-400 tabular-nums">{idx + 1}.</span>
                <UzInput
                  label={idx === 0 ? 'Amount' : undefined}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={row.amount}
                  onChange={(e) => setRow(idx, { amount: e.target.value })}
                  error={tried && !(Number(row.amount) > 0) ? 'Amount' : undefined}
                />
                <UzDatePicker
                  label={idx === 0 ? "Supplier's last date" : undefined}
                  value={fromIsoDay(row.dueDate)}
                  onValueChange={(d) => setRow(idx, { dueDate: isoDay(d) })}
                  minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                  error={
                    tried && !/^\d{4}-\d{2}-\d{2}$/.test(row.dueDate) ? 'Pick a date' : undefined
                  }
                />
                <UzInput
                  label={idx === 0 ? 'Label (optional)' : undefined}
                  placeholder="Deposit / balance"
                  maxLength={60}
                  value={row.label}
                  onChange={(e) => setRow(idx, { label: e.target.value })}
                />
                <button
                  type="button"
                  title="Remove this instalment"
                  disabled={plan.length <= 2}
                  onClick={() => setPlan((rows) => rows.filter((_, i) => i !== idx))}
                  className="mb-2 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:invisible"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <span
                className={cn(
                  planSumOk ? 'text-emerald-700' : 'text-orange-700',
                  !priceOk && 'text-zinc-500',
                )}
              >
                {priceOk
                  ? planSumOk
                    ? `Adds up to the QC price (${money(Number(qcPrice), currency)}).`
                    : `Instalments total ${money(planSum, currency)} - must equal the QC price ${money(Number(qcPrice), currency)} (${planSum < Number(qcPrice) ? 'short' : 'over'} by ${money(Math.abs(Number(qcPrice) - planSum), currency)}).`
                  : 'Enter the QC price first.'}
              </span>
              <UzButton
                size="sm"
                variant="ghost"
                disabled={plan.length >= OPS_INSTALMENTS_MAX}
                onClick={() => setPlan((rows) => [...rows, { amount: '', dueDate: '', label: '' }])}
              >
                + Add instalment
              </UzButton>
            </div>
            <p className="text-[11px] text-zinc-500">
              Every instalment goes to Accounts together when you ask for fund, each with its own
              pay-by. You can issue the ticket / voucher as soon as the first one is paid; Accounts
              keeps paying the rest.
            </p>
          </div>
        ) : null}
      </div>
      <UzInput
        containerClassName="mt-2"
        label="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="mt-3 flex justify-end gap-2">
        <UzButton size="sm" variant="ghost" onClick={onDone} disabled={hold.isPending}>
          Cancel
        </UzButton>
        <UzButton size="sm" loading={hold.isPending} onClick={submit}>
          <PauseCircle className="mr-1.5 h-3.5 w-3.5" /> Save hold
        </UzButton>
      </div>
    </FormShell>
  );
};

const FundForm = ({
  item,
  caseId,
  onDone,
}: {
  item: OpsCaseItem;
  caseId: string;
  onDone: () => void;
}): React.JSX.Element => {
  const fund = useRequestOpsFund(caseId);
  // A payment plan laid out on the hold fixes the amount (its total) and currency.
  const plan = item.instalments.length > 1 ? item.instalments : [];
  const planTotal = plan.reduce((s, i) => s + i.amount, 0);
  const start = plan.length ? planTotal : (item.fundAmount ?? item.qcPrice);
  const [amount, setAmount] = useState(start !== null ? String(start) : '');
  const [currency, setCurrency] = useState<Currency>(
    ((plan[0]?.currency ?? item.fundCurrency ?? item.currency) as Currency) ?? 'INR',
  );
  // Picked from the supplier master; the API keeps the name on the item (what
  // Accounts pays), so the pick resolves to the supplier's name on change.
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState(item.supplierName ?? '');
  const pickSupplier = (id: string | null): void => {
    setSupplierId(id);
    if (!id) {
      setSupplierName('');
      return;
    }
    void suppliersApi
      .getSupplier(id)
      .then((sup) => setSupplierName(sup.name))
      .catch(() => setSupplierName(''));
  };
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const amountOk = Number(amount) > 0;
  const supplierOk = supplierName.trim().length > 0;

  // Same-currency preview against the quote; the server decides with FX.
  const sameCurrency = currency === item.quotedCurrency;
  const delta = sameCurrency && amountOk ? Number(amount) - item.quotedCost : null;

  const submit = (): void => {
    setTried(true);
    if (!amountOk || !supplierOk) return;
    fund.mutate(
      {
        componentId: item.componentId,
        input: {
          amount: Number(amount),
          currency,
          supplierName: supplierName.trim(),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <FormShell title="Ask Accounts for fund" tone="border-sky-200 bg-sky-50/50 text-sky-900">
      <p className="mb-2 text-xs text-sky-900/80">
        Quoted to the customer:{' '}
        <span className="font-semibold tabular-nums">
          {item.quotedCurrency} {new Intl.NumberFormat('en-IN').format(item.quotedCost)}
        </span>
        {item.qcPrice != null ? (
          <>
            {' '}
            · QC price{' '}
            <span className="font-semibold tabular-nums">
              {item.currency} {new Intl.NumberFormat('en-IN').format(item.qcPrice)}
            </span>
          </>
        ) : null}
      </p>
      <div className="grid gap-2 sm:grid-cols-[1fr_96px]">
        <UzInput
          label="Amount to pay the supplier"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          required
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          disabled={plan.length > 0}
          hint={
            plan.length
              ? `Fixed by the payment plan on the hold (${plan.length} instalments) - edit the hold to change it.`
              : undefined
          }
          error={tried && !amountOk ? 'Enter the amount' : undefined}
        />
        <UzSelect<Currency>
          label="Currency"
          value={currency}
          onValueChange={setCurrency}
          options={CURRENCY_OPTIONS}
          disabled={plan.length > 0}
        />
      </div>
      {plan.length ? (
        <ul className="mt-2 space-y-0.5 rounded-lg bg-white/70 px-3 py-2 text-xs text-zinc-700">
          {plan.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-x-2">
              <span className="w-4 text-zinc-400 tabular-nums">{i.seq}.</span>
              <span className="font-semibold tabular-nums">{money(i.amount, i.currency)}</span>
              {i.label ? <span className="text-zinc-500">{i.label}</span> : null}
              <span className="text-zinc-500">· pay by {fmtDate(i.dueDate)}</span>
            </li>
          ))}
          <li className="pt-1 text-[11px] text-zinc-500">
            Accounts gets one request per instalment, each with its own pay-by; you can issue as
            soon as the first one is paid.
          </li>
        </ul>
      ) : null}
      <div className="mt-2">
        <SupplierPicker
          label="Supplier"
          required
          value={supplierId}
          onValueChange={pickSupplier}
          currentName={supplierName}
          hint={
            !supplierId && item.supplierName
              ? 'Pick another supplier to change.'
              : 'Who Accounts should pay - from the supplier master.'
          }
          error={tried && !supplierOk ? 'Pick the supplier' : undefined}
        />
      </div>
      <UzTextarea
        className="mt-2"
        rows={2}
        placeholder="Bank / UPI details or anything Accounts needs (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {delta !== null && delta !== 0 ? (
        <p className={cn('mt-2 text-xs', delta > 0 ? 'text-orange-700' : 'text-emerald-700')}>
          {delta > 0
            ? `${inr(delta)} over the quote. If the lead's total increase goes over INR ${OPS_PRICE_INCREASE_LIMIT_INR}, an Admin approves it before Accounts pays.`
            : `${inr(delta)} under the quote.`}
        </p>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <UzButton size="sm" variant="ghost" onClick={onDone} disabled={fund.isPending}>
          Cancel
        </UzButton>
        <UzButton size="sm" loading={fund.isPending} onClick={submit}>
          <HandCoins className="mr-1.5 h-3.5 w-3.5" /> Ask for fund
        </UzButton>
      </div>
    </FormShell>
  );
};

const IssueForm = ({
  item,
  caseId,
  onDone,
}: {
  item: OpsCaseItem;
  caseId: string;
  onDone: () => void;
}): React.JSX.Element => {
  const issue = useIssueOpsCaseItem(caseId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [reference, setReference] = useState(item.issuedReference ?? '');
  const [files, setFiles] = useState<AttachmentDraft[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [reading, setReading] = useState(false);
  const ok = reference.trim().length > 0 && files.length > 0;

  const addFiles = (picked: FileList | null): void => {
    if (!picked) return;
    const next = [...files];
    for (const f of Array.from(picked)) {
      if (next.length >= OPS_ISSUE_MAX_DOCUMENTS) {
        setFileError(`At most ${OPS_ISSUE_MAX_DOCUMENTS} files per issue`);
        break;
      }
      if (f.size > OPS_DOCUMENT_MAX_BYTES) {
        setFileError(`${f.name}: too large (max 8MB)`);
        continue;
      }
      next.push({ file: f, kind: 'TICKET', audience: 'CUSTOMER' });
    }
    setFiles(next);
  };

  const submit = async (): Promise<void> => {
    setTried(true);
    if (!ok) return;
    setReading(true);
    let documents: OpsDocumentFileInput[] | undefined;
    try {
      documents = files.length
        ? await Promise.all(
            files.map(async (d) => ({
              fileName: d.file.name,
              contentBase64: await fileToBase64(d.file),
              kind: d.kind,
              audience: d.audience,
            })),
          )
        : undefined;
    } catch {
      setFileError('Could not read one of the files');
      setReading(false);
      return;
    }
    setReading(false);
    issue.mutate(
      {
        componentId: item.componentId,
        input: { issuedReference: reference.trim(), ...(documents ? { documents } : {}) },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <FormShell
      title="Issue - supplier confirmed"
      tone="border-emerald-200 bg-emerald-50/50 text-emerald-900"
    >
      <UzInput
        label="Ticket / confirmation number"
        required
        value={reference}
        onChange={(e) => setReference(e.target.value)}
        error={tried && !ok ? 'Enter the confirmation from the supplier' : undefined}
      />
      <div className="mt-2">
        <div className="mb-1 text-sm font-medium text-zinc-700">
          Ticket / voucher files <span className="text-red-600">*</span>
        </div>
        {files.length ? (
          <ul className="mb-2 space-y-1.5">
            {files.map((d, i) => (
              <li
                key={`${d.file.name}-${i}`}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs"
              >
                <Paperclip className="h-3.5 w-3.5 text-zinc-500" />
                <span className="min-w-0 flex-1 truncate font-medium text-zinc-800">
                  {d.file.name}
                </span>
                <UzSelect<Exclude<OpsDocumentKind, 'PAYMENT_PROOF'>>
                  value={d.kind}
                  onValueChange={(kind) =>
                    setFiles((cur) => cur.map((x, j) => (j === i ? { ...x, kind } : x)))
                  }
                  options={KIND_OPTIONS}
                  triggerClassName="h-7 w-36 text-xs"
                />
                <UzSelect<OpsDocumentAudience>
                  value={d.audience}
                  onValueChange={(audience) =>
                    setFiles((cur) => cur.map((x, j) => (j === i ? { ...x, audience } : x)))
                  }
                  options={AUDIENCE_OPTIONS}
                  triggerClassName="h-7 w-32 text-xs"
                />
                <button
                  type="button"
                  title="Remove"
                  className="text-zinc-400 hover:text-rose-600"
                  onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex w-full items-center gap-2 rounded-lg border-2 border-dashed border-zinc-300 bg-white px-3 py-2 text-left text-xs text-zinc-600 hover:bg-zinc-50"
        >
          <Upload className="h-3.5 w-3.5 text-zinc-500" />
          Add e-ticket / voucher (PNG, JPG, WebP, PDF up to 8MB). Marked "for customer" by default.
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={DOC_ACCEPT}
          className="sr-only"
          onChange={(e) => {
            setFileError(null);
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {fileError ? <p className="mt-1 text-xs text-red-600">{fileError}</p> : null}
        {tried && files.length === 0 && !fileError ? (
          <p className="mt-1 text-xs text-red-600">
            Attach the ticket / voucher before marking issued.
          </p>
        ) : null}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <UzButton size="sm" variant="ghost" onClick={onDone} disabled={issue.isPending}>
          Cancel
        </UzButton>
        <UzButton size="sm" loading={issue.isPending || reading} onClick={() => void submit()}>
          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Mark issued
        </UzButton>
      </div>
    </FormShell>
  );
};

const AmendForm = ({
  item,
  caseId,
  onDone,
}: {
  item: OpsCaseItem;
  caseId: string;
  onDone: () => void;
}): React.JSX.Element => {
  const amend = useRequestOpsAmendment(caseId);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const ok = reason.trim().length > 0;

  const submit = (): void => {
    setTried(true);
    if (!ok) return;
    amend.mutate(
      { componentId: item.componentId, input: { reason: reason.trim() } },
      { onSuccess: onDone },
    );
  };

  return (
    <FormShell
      title="Not available - send to seller"
      tone="border-rose-200 bg-rose-50/50 text-rose-900"
    >
      <UzTextarea
        rows={2}
        placeholder="What is not available, and any alternatives the supplier offered"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      {tried && !ok ? (
        <p className="mt-1 text-xs text-red-600">Say what is not available.</p>
      ) : null}
      <p className="mt-1 text-[11px] text-rose-800/80">
        An admin first approves the itinerary change, then the lead's seller is notified to change
        it with the customer. It comes back to you to book once they resolve it.
      </p>
      <div className="mt-3 flex justify-end gap-2">
        <UzButton size="sm" variant="ghost" onClick={onDone} disabled={amend.isPending}>
          Cancel
        </UzButton>
        <UzButton size="sm" variant="destructive" loading={amend.isPending} onClick={submit}>
          <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Send for amendment
        </UzButton>
      </div>
    </FormShell>
  );
};

// ---------------------------------------------------------------------------
// Item card
// ---------------------------------------------------------------------------

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div className="text-[10px] font-semibold text-zinc-500 uppercase">{label}</div>
    {children}
  </div>
);

/**
 * The booking actions for one item - Not available / Hold / Issue (ask for
 * fund) / Issue / reset - with their dialogs. Rendered inside the expanded
 * ItemCard and, compact, straight on the booking table row so the operator
 * acts without opening the row.
 */
export const ItemActions = ({
  item,
  caseId,
  canWork,
  compact = false,
  className,
}: {
  item: OpsCaseItem;
  caseId: string;
  canWork: boolean;
  /** Table-row variant: smaller buttons, short labels. */
  compact?: boolean;
  className?: string;
}): React.JSX.Element | null => {
  const [mode, setMode] = useState<Mode>('none');
  const [confirmReset, setConfirmReset] = useState(false);
  const reset = useResetOpsCaseItem(caseId);
  // 2026-10-30. A desk executive holds, issues and marks items not
  // available; Operations cross-checks the hold and asks for fund (the API
  // enforces the same).
  const executive = useCurrentUser()?.role === 'OPERATIONS_EXECUTIVE';
  const s = item.status;
  const canHold = s === 'PENDING' || s === 'HOLD' || s === 'APPROVAL_REJECTED';
  const canFund = !executive && (s === 'PENDING' || s === 'HOLD' || s === 'APPROVAL_REJECTED');
  // Paid in full, or the first instalment is in (the supplier releases the ticket / voucher on the deposit).
  const canIssue =
    s === 'FUND_PAID' || (s === 'FUND_REQUESTED' && item.instalments.some((i) => i.paidAt));
  const canAmend =
    s === 'PENDING' ||
    s === 'HOLD' ||
    s === 'APPROVAL_PENDING' ||
    s === 'APPROVAL_REJECTED' ||
    s === 'FUND_REQUESTED';
  const canReset = s === 'HOLD' || s === 'APPROVAL_REJECTED';
  // Included in a package (quoted 0): nothing to hold, fund or issue on its own.
  if (!canWork || item.bundled) return null;
  if (!canAmend && !canHold && !canFund && !canIssue && !canReset) return null;
  // No "xs" size on UzButton: the compact row variant shrinks via classes.
  const btn = 'sm' as const;
  const compactCls = compact ? 'h-7 px-2 text-xs' : undefined;
  const icon = compact ? 'mr-1 h-3 w-3' : 'mr-1.5 h-3.5 w-3.5';

  return (
    // On a table row neither the buttons nor the dialogs (React portals still
    // bubble to this tree) may toggle the row open.
    <div className="contents" onClick={(e) => e.stopPropagation()}>
      <div className={cn('flex flex-wrap justify-end gap-1.5', className)}>
        {canAmend ? (
          <UzButton
            size={btn}
            variant="ghost"
            className={cn('text-rose-700', compactCls)}
            onClick={() => setMode('amend')}
          >
            <Undo2 className={icon} /> Not available
          </UzButton>
        ) : null}
        {canHold ? (
          <UzButton
            size={btn}
            variant="outline"
            className={compactCls}
            onClick={() => setMode('hold')}
          >
            <PauseCircle className={icon} />
            {s === 'HOLD' ? 'Edit hold' : 'Hold'}
          </UzButton>
        ) : null}
        {canFund ? (
          <UzButton size={btn} className={compactCls} onClick={() => setMode('fund')}>
            <HandCoins className={icon} /> {compact ? 'Ask for fund' : 'Issue · ask for fund'}
          </UzButton>
        ) : null}
        {canIssue ? (
          <UzButton size={btn} className={compactCls} onClick={() => setMode('issue')}>
            <CheckCircle2 className={icon} /> Issue
          </UzButton>
        ) : null}
        {canReset ? (
          <UzButton
            size={btn}
            variant="ghost"
            className={compactCls}
            title="Back to to-book"
            onClick={() => setConfirmReset(true)}
          >
            <RotateCcw className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
          </UzButton>
        ) : null}
      </div>
      <UzDialog
        open={mode !== 'none'}
        onOpenChange={(o) => !o && setMode('none')}
        title={mode !== 'none' ? MODE_TITLE[mode].title : undefined}
        description={
          mode !== 'none' ? (
            <span>
              {item.title}
              {item.dayIndex ? ` · Day ${item.dayIndex}` : ''} · {MODE_TITLE[mode].description}
            </span>
          ) : undefined
        }
        size="xl"
      >
        {mode === 'hold' ? (
          <HoldForm item={item} caseId={caseId} onDone={() => setMode('none')} />
        ) : null}
        {mode === 'fund' ? (
          <FundForm item={item} caseId={caseId} onDone={() => setMode('none')} />
        ) : null}
        {mode === 'issue' ? (
          <IssueForm item={item} caseId={caseId} onDone={() => setMode('none')} />
        ) : null}
        {mode === 'amend' ? (
          <AmendForm item={item} caseId={caseId} onDone={() => setMode('none')} />
        ) : null}
      </UzDialog>
      <UzConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Move this item back to 'to book'?"
        description="The hold details saved here are cleared. Nothing is changed with the supplier."
        confirmLabel="Reset"
        tone="destructive"
        onConfirm={() => reset.mutateAsync(item.componentId).then(() => undefined)}
      />
    </div>
  );
};

export const ItemCard = ({
  item,
  caseId,
  canWork,
  details,
}: {
  item: OpsCaseItem;
  caseId: string;
  canWork: boolean;
  /** Booking facts block (full-page screen); the drawer passes nothing. */
  details?: React.ReactNode;
}): React.JSX.Element => {
  const proof = useOpenOpsProof();
  const kind = {
    label: itemKindLabel(item.kind, item.vasKind),
    icon: itemKindIcon(item.kind, item.vasKind),
  };
  const st = STATUS_META[item.status];
  const Icon = kind.icon;
  const dates = [fmtDate(item.startDate), item.endDate ? fmtDate(item.endDate) : '']
    .filter(Boolean)
    .join(' - ');
  const route =
    item.fromLocation || item.toLocation || item.details?.transfer?.cityName
      ? transferRouteLabel(item)
      : null;

  const s = item.status;

  return (
    <li className={cn('rounded-xl border bg-white p-3.5', st.border)}>
      <div className="flex items-start gap-3">
        <span
          className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', st.icon)}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold tracking-wide text-zinc-500 uppercase">
              {kind.label}
              {item.dayIndex ? ` · Day ${item.dayIndex}` : ''}
            </span>
            {item.bundled ? (
              <UzBadge tone="secondary">In package</UzBadge>
            ) : (
              <UzBadge tone={st.tone}>{st.label}</UzBadge>
            )}
          </div>
          <div className="mt-0.5 text-sm font-medium text-zinc-900">{item.title}</div>
          <div className="text-xs text-zinc-500">
            {[dates, route].filter(Boolean).join(' · ')}
            {item.quotedCost > 0
              ? `${dates || route ? ' · ' : ''}Quoted ${money(item.quotedCost, item.quotedCurrency)}`
              : ''}
          </div>

          {/* Hold facts stay visible until the supplier is paid. */}
          {item.holdReference && s !== 'ISSUED' && s !== 'FUND_PAID' ? (
            <div className="mt-2 grid gap-1 rounded-lg bg-amber-50/60 px-3 py-2 text-xs text-zinc-700 sm:grid-cols-3">
              <Fact label="QC price">{money(item.qcPrice, item.currency)}</Fact>
              <Fact label="Holding no.">
                <span className="font-medium">{item.holdReference}</span>
              </Fact>
              <Fact label="Pay by">
                {item.paymentDueDate ? (
                  <>
                    {fmtDate(item.paymentDueDate)}{' '}
                    <span
                      className={cn(
                        'ml-1 rounded px-1.5 py-px text-[10px] font-semibold ring-1',
                        payByTone(item.paymentDueDate).className,
                      )}
                    >
                      {payByTone(item.paymentDueDate).label}
                    </span>
                  </>
                ) : (
                  '-'
                )}
              </Fact>
            </div>
          ) : null}

          {/* The supplier payment plan, with where it stands. */}
          {item.instalments.length > 1 ? (
            <div className="mt-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-700">
              <div className="mb-1 text-[10px] font-semibold tracking-wide text-zinc-500 uppercase">
                Payment plan · {item.instalments.filter((i) => i.paidAt).length} of{' '}
                {item.instalments.length} paid
              </div>
              <ul className="space-y-0.5">
                {item.instalments.map((i) => (
                  <li key={i.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span
                      className={cn(
                        'h-1.5 w-1.5 shrink-0 rounded-full',
                        i.paidAt ? 'bg-emerald-500' : 'bg-zinc-300',
                      )}
                    />
                    <span className="w-4 text-zinc-400 tabular-nums">{i.seq}.</span>
                    <span className="font-semibold tabular-nums">
                      {money(i.amount, i.currency)}
                    </span>
                    {i.label ? <span className="text-zinc-500">{i.label}</span> : null}
                    {i.paidAt ? (
                      <span className="text-emerald-700">
                        paid {fmtDate(i.paidAt)}
                        {i.paymentReference ? ` · ref ${i.paymentReference}` : ''}
                      </span>
                    ) : (
                      <span className="text-zinc-500">
                        · pay by {fmtDate(i.dueDate)}{' '}
                        <span
                          className={cn(
                            'rounded px-1 py-px text-[10px] font-semibold ring-1',
                            payByTone(i.dueDate).className,
                          )}
                        >
                          {payByTone(i.dueDate).label}
                        </span>
                      </span>
                    )}
                    {i.hasPaymentProof ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-medium text-sky-700 hover:underline"
                        onClick={() =>
                          proof.mutate({
                            caseId,
                            componentId: item.componentId,
                            instalmentId: i.id,
                          })
                        }
                      >
                        <FileImage className="h-3 w-3" /> screenshot
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Fund request facts. */}
          {details ? <div className="mt-3">{details}</div> : null}

          {item.fundAmount !== null && s !== 'HOLD' && s !== 'PENDING' ? (
            <div className="mt-2 grid gap-1 rounded-lg bg-sky-50/60 px-3 py-2 text-xs text-zinc-700 sm:grid-cols-3">
              <Fact label="Fund asked">{money(item.fundAmount, item.fundCurrency ?? 'INR')}</Fact>
              <Fact label="Supplier">{item.supplierName ?? '-'}</Fact>
              <Fact label="Vs quote">
                {item.priceDeltaInr === null ? (
                  '-'
                ) : item.priceDeltaInr > 0 ? (
                  <span className="text-orange-700">+{inr(item.priceDeltaInr)}</span>
                ) : (
                  <span className="text-emerald-700">-{inr(item.priceDeltaInr)}</span>
                )}
              </Fact>
            </div>
          ) : null}

          {s === 'APPROVAL_PENDING' ? (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-orange-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {item.approvalReason === 'OVER_RECEIPTS' ? (
                <>
                  Supplier payments asked for this lead go{' '}
                  {item.approvalShortfallInr !== null ? inr(item.approvalShortfallInr) : ''} beyond
                  what the customer has paid so far. Waiting for an Admin before Accounts pays.
                </>
              ) : (
                <>
                  Lead's total increase is{' '}
                  {item.approvalIncreaseInr !== null ? inr(item.approvalIncreaseInr) : 'unknown'}{' '}
                  (limit INR {OPS_PRICE_INCREASE_LIMIT_INR}). Waiting for an Admin's approval.
                </>
              )}
            </p>
          ) : null}
          {s === 'APPROVAL_REJECTED' ? (
            <p className="mt-2 text-xs text-red-700">
              Rejected{item.approvalDecidedByName ? ` by ${item.approvalDecidedByName}` : ''}
              {item.approvalNote ? `: ${item.approvalNote}` : ''}. Re-quote and ask again, or send
              it for amendment.
            </p>
          ) : null}
          {s === 'FUND_PAID' || (s === 'ISSUED' && item.paidAt) ? (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-600">
              <span>
                Paid {fmtDate(item.paidAt)}
                {item.paymentReference ? ` · Ref ${item.paymentReference}` : ''}
              </span>
              {item.hasPaymentProof ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-medium text-sky-700 hover:underline"
                  onClick={() => proof.mutate({ caseId, componentId: item.componentId })}
                >
                  <FileImage className="h-3.5 w-3.5" /> Payment screenshot
                </button>
              ) : null}
            </div>
          ) : null}
          {s === 'ISSUED' ? (
            <div className="mt-1 text-xs text-zinc-600">
              Confirmation <span className="font-medium">{item.issuedReference ?? '-'}</span>
              {item.issuedByName ? ` · by ${item.issuedByName}` : ''} · {fmtDate(item.issuedAt)}
            </div>
          ) : null}
          {s === 'AMENDMENT' ? (
            <p className="mt-2 text-xs text-rose-800">
              Sent to the seller {fmtDate(item.amendmentRequestedAt)}: {item.amendmentReason}
            </p>
          ) : null}
          {s === 'AMENDMENT_APPROVAL' ? (
            <p className="mt-2 text-xs text-rose-800">
              Waiting for an admin to approve the itinerary change (
              {fmtDate(item.amendmentRequestedAt)}): {item.amendmentReason}
            </p>
          ) : null}
          {item.note && s !== 'AMENDMENT' && s !== 'AMENDMENT_APPROVAL' ? (
            <div className="mt-1 text-xs text-zinc-500 italic">{item.note}</div>
          ) : null}
        </div>
      </div>

      <ItemActions item={item} caseId={caseId} canWork={canWork} className="mt-3" />
    </li>
  );
};

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

/**
 * Collapsible "Documents" block, three tabs: the case's files for the
 * customer (tickets / vouchers from ops), its internal files (payment proofs
 * from Accounts), both with upload and remove for whoever can work the case;
 * and the seller's screenshots from the lead, read-only, with their notes.
 */
export const CaseDocuments = ({
  caseId,
  canWork,
  items,
  defaultExpanded = false,
}: {
  caseId: string;
  canWork: boolean;
  items: OpsCaseItem[];
  /** Full-page screen opens it; the drawer keeps it folded. */
  defaultExpanded?: boolean;
}): React.JSX.Element => {
  const docs = useOpsCaseDocuments(caseId);
  const seller = useOpsSellerScreenshots(caseId);
  const openShot = useOpenOpsSellerScreenshot();
  const upload = useUploadOpsCaseDocument(caseId);
  const remove = useDeleteOpsCaseDocument(caseId);
  const open = useOpenOpsDocument();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [uploading, setUploading] = useState(false);
  const all = docs.data ?? [];
  const forCustomer = all.filter((d) => d.audience === 'CUSTOMER').length;
  const shots = seller.data ?? [];

  const uploadControl = canWork ? (
    uploading ? (
      <OpsDocumentUploadForm
        items={items.map((i) => ({ componentId: i.componentId, title: i.title }))}
        pending={upload.isPending}
        onSubmit={(input) => upload.mutate(input, { onSuccess: () => setUploading(false) })}
        onCancel={() => setUploading(false)}
      />
    ) : (
      <UzButton size="sm" variant="outline" onClick={() => setUploading(true)}>
        <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload a document
      </UzButton>
    )
  ) : null;

  /** One tab per audience: that audience's files, then the upload control. */
  const docsTab = (audience: OpsDocumentAudience) => {
    const ofAudience = all.filter((d) => d.audience === audience);
    const customer = audience === 'CUSTOMER';
    return {
      value: audience,
      label: customer ? 'For the customer' : 'Internal',
      icon: customer ? <Send className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />,
      count: ofAudience.length,
      content: (
        <div className="space-y-3 pt-3">
          {docs.isLoading ? (
            <DocumentsLoading />
          ) : (
            <OpsDocumentsGroup
              title={customer ? 'For the customer' : 'Internal'}
              icon={customer ? <Send className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
              hint={
                customer
                  ? 'Tickets and vouchers to send to the customer show up here once ops issues the bookings.'
                  : "Accounts' payment proofs and supplier invoices stay here."
              }
              docs={ofAudience}
              onOpen={(d) => open.mutate({ caseId, docId: d.id })}
              onDelete={canWork ? (d) => remove.mutate(d.id) : undefined}
              busy={remove.isPending}
            />
          )}
          {uploadControl}
        </div>
      ),
    };
  };

  return (
    <section className="rounded-lg border border-zinc-200 bg-white">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm"
      >
        <span className="flex items-center gap-2 font-medium text-zinc-900">
          <FolderOpen className="h-4 w-4 text-zinc-500" />
          Documents
          <span className="text-xs font-normal text-zinc-500">
            {docs.isLoading
              ? '…'
              : all.length === 0 && shots.length === 0
                ? 'none yet'
                : `${all.length} file${all.length === 1 ? '' : 's'} · ${forCustomer} for customer` +
                  (shots.length ? ` · ${shots.length} from seller` : '')}
          </span>
        </span>
        <span className="text-xs text-zinc-500">{expanded ? 'Hide' : 'Show'}</span>
      </button>
      {expanded ? (
        <div className="border-t border-zinc-100 px-3 py-3">
          <UzTabs
            tabs={[
              docsTab('CUSTOMER'),
              docsTab('INTERNAL'),
              {
                value: 'seller',
                label: 'From the seller',
                icon: <FileImage className="h-3.5 w-3.5" />,
                count: shots.length,
                content: (
                  <div className="pt-3">
                    {/* The seller's screenshots from the lead's Screenshots tab (long-haul leads). */}
                    {seller.isLoading ? (
                      <DocumentsLoading />
                    ) : shots.length === 0 ? (
                      <p className="text-sm text-zinc-500">
                        Nothing from the seller yet. Screenshots they attach on the lead show up
                        here.
                      </p>
                    ) : (
                      <ul className="space-y-1.5">
                        {shots.map((s) => (
                          <li
                            key={s.id}
                            className="flex items-start gap-2.5 rounded-lg border border-zinc-200 bg-white px-3 py-2"
                          >
                            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-600">
                              <FileImage className="h-4 w-4" />
                            </span>
                            <div className="min-w-0 flex-1 text-sm">
                              <div className="truncate font-medium text-zinc-900">{s.fileName}</div>
                              <div className="truncate text-xs text-zinc-500">
                                {s.componentTitle ? `${s.componentTitle} · ` : ''}
                                {s.uploadedByName} ·{' '}
                                {s.createdAt.toLocaleString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                              </div>
                              {s.note ? (
                                <p className="mt-1 border-l-2 border-zinc-300 pl-2 text-xs whitespace-pre-wrap text-zinc-700">
                                  {s.note}
                                </p>
                              ) : null}
                            </div>
                            <UzButton
                              size="sm"
                              variant="ghost"
                              title="Open"
                              disabled={openShot.isPending}
                              onClick={() => openShot.mutate({ caseId, screenshotId: s.id })}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </UzButton>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </div>
      ) : null}
    </section>
  );
};

export const OpsBookingsDrawer = ({
  row,
  canWork,
  isManager = false,
  onOpenChange,
  onOpenLead,
}: {
  row: OpsCaseRow | null;
  canWork: boolean;
  /** Managers may book before the desk is dealt. */
  isManager?: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenLead: (row: OpsCaseRow) => void;
}): React.JSX.Element => {
  const items = useOpsCaseItems(row?.id ?? null);
  const list = items.data ?? [];
  const count = (pred: (i: OpsCaseItem) => boolean): number => list.filter(pred).length;
  const issued = count((i) => i.status === 'ISSUED');
  const held = count((i) => i.status === 'HOLD');
  const inMoney = count((i) =>
    ['APPROVAL_PENDING', 'FUND_REQUESTED', 'FUND_PAID'].includes(i.status),
  );
  const amend = count(
    (i) =>
      i.status === 'AMENDMENT' ||
      i.status === 'AMENDMENT_APPROVAL' ||
      i.status === 'APPROVAL_REJECTED',
  );
  const toBook = count((i) => i.status === 'PENDING');
  const workable =
    canWork && row !== null && row.status !== 'CANCELLED' && (row.status !== 'QUEUED' || isManager);

  return (
    <UzDrawer
      open={row !== null}
      onOpenChange={onOpenChange}
      contentClassName="sm:max-w-[680px]"
      title={row ? `Bookings · ${row.customerName}` : 'Bookings'}
      description={
        row
          ? `${row.leadReference}${row.tripTitle ? ` · ${row.tripTitle}` : ''}${
              row.travellerCount ? ` · ${row.travellerCount} pax` : ''
            }`
          : undefined
      }
    >
      {row ? (
        <div className="flex h-full flex-col gap-3 overflow-y-auto pb-6">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
            <span className="flex flex-wrap items-center gap-3">
              <span>
                <b className="text-zinc-900">{toBook}</b> to book
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock3 className="h-3 w-3 text-amber-600" />
                <b className="text-zinc-900">{held}</b> on hold
              </span>
              <span>
                <b className="text-zinc-900">{inMoney}</b> in payment
              </span>
              {amend ? (
                <span className="text-rose-700">
                  <b>{amend}</b> need attention
                </span>
              ) : null}
              <span>
                <b className="text-zinc-900">{issued}</b> issued
              </span>
            </span>
            <UzButton size="sm" variant="ghost" onClick={() => onOpenLead(row)}>
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open lead
            </UzButton>
          </div>

          {canWork && row.status === 'QUEUED' ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              This lead is still unassigned. Assign it to someone before booking.
            </p>
          ) : null}

          <CaseDocuments caseId={row.id} canWork={workable} items={list} />

          {items.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-zinc-500">
              <UzSpinner size="sm" /> Loading items…
            </div>
          ) : list.length === 0 ? (
            <p className="py-6 text-sm text-zinc-500">
              The converted itinerary has no bookable items.
            </p>
          ) : (
            <ul className="space-y-2.5">
              {list.map((item) => (
                <ItemCard
                  key={item.componentId}
                  item={item}
                  caseId={item.caseId}
                  canWork={workable}
                />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </UzDrawer>
  );
};
